import React, { useState } from "react";
import WizardPageWrapper, { GeneralPageSummary } from "../WizardPageWrapper";
import Flex from "../../../../../../components/Flex";
import { Form, Input, Typography, Checkbox, Radio } from "antd";
import FormGroupItem from "../FormGroupItem";
import { CoverWizardForm, FormGroupTouched } from "../../CoverWizardModal";
import { formatInTitleCase } from "../../../../../../util/stringUtils";

type SpaCoverPageProps = {
  onNextButtonClick?: () => void;
  onBackButtonClick?: () => void;
  //the current form values are needed in order to have field values at mount (useWatch outputs current values at second rerender)
  initialFormValues?: CoverWizardForm;
  formGroupTouchedState: [FormGroupTouched, React.Dispatch<FormGroupTouched>];
};

const yesOrNoRadios = [
  { label: "Yes", value: "yes" },
  { label: "No", value: "no" },
];

const spaCoverWantedFieldName = "spaCoverWanted";

const wallOrientationFieldName = "spaWallOrientation";
const spaPoolHeightDifferenceFieldName = "spaPoolHeightDifference";
const wallOrientationRadios = [
  { label: "Lower", value: "lower" },
  { label: "Higher", value: "higher" },
  { label: "N/A", value: "n/a" },
];

const spaHasOpenSpillwayFieldName = "spaHasOpenSpillway";
const spillwayWidthLocationFieldName = "spaSpillwayWidthLocation";

const spaCopingSizeFieldName = "spaCopingSize";
const spaCopingOdFieldName = "spaCopingOd";
const spaCopingIdFieldName = "spaCopingId";

const spaDesignPreferencesFieldName = "spaDesignPreferences";
const designPreferancesCheckboxes = [
  {
    label: "With long straps to anchor into deck (Max 16” height for safety)",
    value: "With long straps to anchor into deck (Max 16” height for safety)",
  },
  {
    label: "Anchor into outside wall of spa",
    value: "Anchor into outside wall of spa",
  },
  {
    label: "With split skirt and 2 straps (no cable - cap style)",
    value: "With split skirt and 2 straps (no cable - cap style)",
  },
  {
    label: "Closed with cable and snap hook (cap style)",
    value: "Closed with cable and snap hook (cap style)",
  },
  {
    label: 'Superflap (applicable and height not beyond 12")',
    value: 'Superflap (applicable and height not beyond 12")',
  },
];

export default function SpaCoverPage({
  onNextButtonClick,
  onBackButtonClick,
  initialFormValues,
  formGroupTouchedState: [formGroupTouched, setFormGroupTouched],
}: SpaCoverPageProps) {
  const form = Form.useFormInstance<CoverWizardForm>();

  //---------spa cover wanted group state
  const spaCoverWantedAnswer =
    Form.useWatch(spaCoverWantedFieldName, { form, preserve: true }) ??
    initialFormValues?.[spaCoverWantedFieldName];
  const spaCoverWanted = spaCoverWantedAnswer === "yes";

  //---------spa wall offset group state
  const wallOrientationAnswer =
    Form.useWatch(wallOrientationFieldName, { form, preserve: true }) ??
    initialFormValues?.[wallOrientationFieldName];
  const spaPoolHeightDifference =
    Form.useWatch(spaPoolHeightDifferenceFieldName, {
      form: form,
      preserve: true,
    }) ?? initialFormValues?.[spaPoolHeightDifferenceFieldName];
  const spaIsHigherOrLower =
    wallOrientationAnswer === "lower" || wallOrientationAnswer === "higher";

  //---------spa spillway group state
  const spaHasSpillwayAnswer =
    Form.useWatch(spaHasOpenSpillwayFieldName, { form, preserve: true }) ??
    initialFormValues?.[spaHasOpenSpillwayFieldName];
  const spillwayWidthLocation =
    Form.useWatch(spillwayWidthLocationFieldName, {
      form: form,
      preserve: true,
    }) ?? initialFormValues?.[spillwayWidthLocationFieldName];
  const spaHasSpillway = spaHasSpillwayAnswer === "yes";

  //---------spa coping group state
  const spaCopingSize =
    Form.useWatch(spaCopingSizeFieldName, { form, preserve: true }) ??
    initialFormValues?.[spaCopingSizeFieldName];
  const spaCopingOd =
    Form.useWatch(spaCopingOdFieldName, { form, preserve: true }) ??
    initialFormValues?.[spaCopingOdFieldName];
  const spaCopingId =
    Form.useWatch(spaCopingIdFieldName, { form, preserve: true }) ??
    initialFormValues?.[spaCopingIdFieldName];

  //---------design preferences group state
  const designPreferences =
    Form.useWatch(spaDesignPreferencesFieldName, { form, preserve: true }) ??
    initialFormValues?.[spaDesignPreferencesFieldName];

  return (
    <WizardPageWrapper
      title="Spa Cover"
      summary={<GeneralPageSummary />}
      onBackButtonClick={onBackButtonClick}
      onNextButtonClick={onNextButtonClick}
    >
      <Flex vertical gap="16px">
        <FormGroupItem
          label="Spa Cover"
          closeButtonText={formGroupTouched.spaCoverGroup ? "Save" : "Next"}
          onGroupClosed={() =>
            setFormGroupTouched({ ...formGroupTouched, spaCoverGroup: true })
          }
          notExpandedDisplayValue={formatInTitleCase(spaCoverWantedAnswer)}
          expandedOnMount={!formGroupTouched.spaCoverGroup}
        >
          <Flex vertical gap="0.5rem">
            <Typography.Text>Spa cover wanted?</Typography.Text>
            <Radio.Group
              options={yesOrNoRadios}
              value={spaCoverWantedAnswer}
              onChange={(event) => {
                const newValue = event.target.value;
                if (newValue === "no") {
                  return form.setFieldsValue({
                    [spaCoverWantedFieldName]: newValue,
                    [wallOrientationFieldName]: "n/a",
                    [spaPoolHeightDifferenceFieldName]: "",
                    [spaHasOpenSpillwayFieldName]: "no",
                    [spillwayWidthLocationFieldName]: "",
                    [spaCopingSizeFieldName]: "",
                    [spaCopingOdFieldName]: "",
                    [spaCopingIdFieldName]: "",
                    [spaDesignPreferencesFieldName]: [],
                  });
                }
                form.setFieldValue(spaCoverWantedFieldName, newValue);
              }}
            />
          </Flex>
        </FormGroupItem>
        {spaCoverWanted && formGroupTouched.spaCoverGroup && (
          <FormGroupItem
            label="Wall Offset Distance"
            closeButtonText={
              formGroupTouched.spaWallOffsetGroup ? "Save" : "Next"
            }
            onGroupClosed={() =>
              setFormGroupTouched({
                ...formGroupTouched,
                spaWallOffsetGroup: true,
              })
            }
            notExpandedDisplayValue={
              spaIsHigherOrLower && !!spaPoolHeightDifference
                ? spaPoolHeightDifference
                : "None"
            }
            expandedOnMount={!formGroupTouched.spaWallOffsetGroup}
          >
            <Flex vertical gap="0.4rem">
              <Flex vertical gap="0.5rem">
                <Typography.Text>
                  Is the spa wall lower or higher?
                </Typography.Text>
                <Radio.Group
                  options={wallOrientationRadios}
                  value={wallOrientationAnswer}
                  onChange={(event) => {
                    const newValue = event.target.value;
                    if (newValue === "no") {
                      return form.setFieldsValue({
                        [wallOrientationFieldName]: newValue,
                        [spaPoolHeightDifferenceFieldName]: "",
                      });
                    }
                    form.setFieldValue(wallOrientationFieldName, newValue);
                  }}
                />
              </Flex>
              {spaIsHigherOrLower && (
                <Form.Item<CoverWizardForm>
                  style={{ margin: "0" }}
                  name={spaPoolHeightDifferenceFieldName}
                  label="How much higher or lower is it from the pool surface?"
                >
                  <Input style={{ maxWidth: "282px", width: "100%" }} />
                </Form.Item>
              )}
            </Flex>
          </FormGroupItem>
        )}
        {spaCoverWanted && formGroupTouched.spaWallOffsetGroup && (
          <FormGroupItem
            label="Spa Spillway"
            closeButtonText={
              formGroupTouched.spaWallOffsetGroup ? "Save" : "Next"
            }
            onGroupClosed={() =>
              setFormGroupTouched({
                ...formGroupTouched,
                spaSpillwayGroup: true,
              })
            }
            notExpandedDisplayValue={
              spaHasSpillway && !!spillwayWidthLocation
                ? spillwayWidthLocation
                : spaHasSpillway
                ? "Yes"
                : "None"
            }
            expandedOnMount={!formGroupTouched.spaSpillwayGroup}
          >
            <Flex vertical gap="0.4rem">
              <Flex vertical gap="0.5rem">
                <Typography.Text>
                  Does the spa have an open spillway?
                </Typography.Text>
                <Radio.Group
                  options={yesOrNoRadios}
                  value={spaHasSpillwayAnswer}
                  onChange={(event) => {
                    const newValue = event.target.value;
                    if (newValue === "no") {
                      return form.setFieldsValue({
                        [spaHasOpenSpillwayFieldName]: newValue,
                        [spillwayWidthLocationFieldName]: "",
                      });
                    }
                    form.setFieldValue(spaHasOpenSpillwayFieldName, newValue);
                  }}
                />
              </Flex>
              {spaHasSpillway && (
                <Form.Item<CoverWizardForm>
                  style={{ margin: "0" }}
                  name={spillwayWidthLocationFieldName}
                  label="Width, location"
                >
                  <Input style={{ maxWidth: "282px", width: "100%" }} />
                </Form.Item>
              )}
            </Flex>
          </FormGroupItem>
        )}
        {spaCoverWanted && formGroupTouched.spaSpillwayGroup && (
          <FormGroupItem
            label="Spa Coping"
            closeButtonText={
              formGroupTouched.spaSpillwayGroup ? "Save" : "Next"
            }
            onGroupClosed={() =>
              setFormGroupTouched({ ...formGroupTouched, spaCopingGroup: true })
            }
            notExpandedDisplayValue={`Size: ${
              !!spaCopingSize ? spaCopingSize : "None"
            }, OD: ${!!spaCopingOd ? spaCopingOd : "None"}, ID: ${
              !!spaCopingId ? spaCopingId : "None"
            }`}
            expandedOnMount={!formGroupTouched.spaCopingGroup}
          >
            <Flex vertical gap="0.4rem">
              <Form.Item<CoverWizardForm>
                style={{ margin: "0" }}
                name={spaCopingSizeFieldName}
                label="Spa Coping Size"
              >
                <Input style={{ width: "100%" }} />
              </Form.Item>
              <Flex gap="1rem" style={{ width: "100%" }}>
                <Form.Item<CoverWizardForm>
                  style={{ margin: "0" }}
                  name={spaCopingOdFieldName}
                  label="Outer Diameter"
                >
                  <Input style={{ width: "100%" }} />
                </Form.Item>
                <Form.Item<CoverWizardForm>
                  style={{ margin: "0" }}
                  name={spaCopingIdFieldName}
                  label="Inner Diameter"
                >
                  <Input style={{ width: "100%" }} />
                </Form.Item>
              </Flex>
            </Flex>
          </FormGroupItem>
        )}
        {spaCoverWanted && formGroupTouched.spaCopingGroup && (
          <FormGroupItem
            label="Spa Design Preferences"
            closeButtonText="Save"
            notExpandedDisplayValue={
              designPreferences.length ? designPreferences.join(", ") : "None"
            }
            onGroupClosed={() =>
              setFormGroupTouched({
                ...formGroupTouched,
                spaDesignPreferencesGroup: true,
              })
            }
            expandedOnMount={!formGroupTouched.spaDesignPreferencesGroup}
          >
            <Flex vertical gap="0.4rem">
              <Form.Item<CoverWizardForm>
                style={{ margin: "0" }}
                name={spaDesignPreferencesFieldName}
              >
                <Checkbox.Group options={designPreferancesCheckboxes} />
              </Form.Item>
            </Flex>
          </FormGroupItem>
        )}
      </Flex>
    </WizardPageWrapper>
  );
}
